import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import {
   DialogActions,
   DialogContent,
   DialogTitle,
   FormControl,
   FormControlLabel,
   Radio,
   RadioGroup,
   Step,
   StepLabel,
   Stepper,
} from '@mui/material';
import { addDays, format } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { addUserRoster } from '../../../axios/RosterItem/RosterItemApi';
import RosterApi from '../../../axios/Roster/RosterApi';
import { getUserOfRoster } from '../../../axios/User/UserApi';
import CustomDateTimePicker from '../../../components/CustomDateTimeRangePicker';
import CustomInput from '../../../components/CustomInput';
import {
   ControlpanelPrimaryButton,
   ControlpanelSecondaryButton,
   ControlpanelSuccess3thButton,
   GrayIconButton,
} from '../../../components/StyledButton';
import { CustomDialog } from '../../../components/StyledComponents';
import { useLoading } from '../../../context/LoadingContext';
import { useSettings } from '../../../context/SettingsContext';
import {
   RosterUsersGroupsStep,
   buildUsersRosterPayload,
   mapRosterUsersToFormData,
   sanitizeUsersFormData,
} from './AddUserRosterWhenCreateRoster';

const STEP_COUNT = 2;

const stepperSx = {
   '& .MuiStepLabel-label.Mui-active': {
      color: '#0057FF',
      fontWeight: 600,
   },
   '& .MuiStepLabel-label.Mui-completed': {
      color: '#0057FF',
      fontWeight: 600,
   },
   '& .MuiStepIcon-root.Mui-active': {
      color: '#0057FF',
   },
   '& .MuiStepIcon-root.Mui-completed': {
      color: '#0057FF',
   },
   '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line, & .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
      borderColor: '#0057FF',
   },
};

const initialUsersFormData = {
   userId: [],
   groupId: [],
};

const EditRosterDialog = (props) => {
   const { t } = useTranslation();
   const {
      onClose,
      selectedEditRoster,
      onRefresh,
      handleRefreshDataAddRoster,
      allUser,
      GroupData,
   } = props;
   const [show, hide] = useLoading();
   const { updateRoster } = RosterApi();
   const { settings } = useSettings();
   const [activeStep, setActiveStep] = useState(0);
   const [rosterIdAddUser, setRosterIdAddUser] = useState('');
   const [usersFormData, setUsersFormData] = useState(initialUsersFormData);

   const [formData, setFormData] = useState({
      rosterName: '',
      repetition: 1,
      startDate: new Date(),
      endDate: new Date(),
   });

   const [validationMessages, setValidationMessages] = useState({
      rosterName: '',
      repetition: '',
      startDate: '',
      endDate: '',
   });

   const [isError, setIsError] = useState({
      rosterName: null,
      repetition: null,
      endDate: null,
   });

   const [isValid, setIsValid] = useState(false);
   const [endOfRepitition, setEndOfRepitition] = useState(false);

   const steps = useMemo(
      () => [t('rosterWizard.roster-details'), t('rosterWizard.add-users-groups')],
      [t]
   );

   useEffect(() => {
      if (!selectedEditRoster) return;

      setFormData({
         rosterName: selectedEditRoster.rosterName || '',
         repetition: selectedEditRoster.repetition || 1,
         startDate: new Date(selectedEditRoster.startDate),
         endDate: new Date(
            selectedEditRoster.endBy ? selectedEditRoster.endBy : selectedEditRoster.startDate
         ),
      });
      setEndOfRepitition(!!selectedEditRoster.endBy);
      setRosterIdAddUser(selectedEditRoster.rosterId || '');
      setActiveStep(0);
      setValidationMessages({
         rosterName: '',
         repetition: '',
         startDate: '',
         endDate: '',
      });
   }, [selectedEditRoster]);

   useEffect(() => {
      if (!selectedEditRoster?.rosterId) return;
      // Tour fake roster — không gọi API
      if (selectedEditRoster.rosterId === "tour-fake-roster") {
         setUsersFormData(initialUsersFormData);
         return;
      }

      let cancelled = false;

      const loadRosterUsers = async () => {
         try {
            const response = await getUserOfRoster({
               rosterId: selectedEditRoster.rosterId,
               langId: localStorage.getItem('i18nextLngId'),
            });

            if (cancelled) return;

            const mapped = mapRosterUsersToFormData(response.data.data || []);
            setUsersFormData(sanitizeUsersFormData(mapped, allUser, GroupData));
         } catch (error) {
            if (!cancelled && error.response?.status !== 401) {
               toast.error(t('common.error'));
            }
         }
      };

      loadRosterUsers();

      return () => {
         cancelled = true;
      };
   }, [selectedEditRoster?.rosterId, t]);

   useEffect(() => {
      setUsersFormData((prev) => sanitizeUsersFormData(prev, allUser, GroupData));
   }, [allUser, GroupData]);

   useEffect(() => {
      const { rosterName, repetition, endDate, startDate } = formData;

      const isRosterNameValid =
         rosterName !== '' && validationMessages.rosterName === '';

      const isRepetitionValid =
         repetition !== '' &&
         !isNaN(repetition) &&
         parseInt(repetition) <= 5 &&
         validationMessages.repetition === '';

      const isStartDateValid = !!startDate;

      const isEndDateValid =
         !endOfRepitition ||
         (endDate && validationMessages.endDate === '' && endDate > startDate);

      setIsError((prevError) => ({
         ...prevError,
         rosterName: isRosterNameValid ? null : true,
         repetition: isRepetitionValid ? null : true,
         endDate: isEndDateValid ? null : true,
      }));

      setIsValid(
         isRosterNameValid &&
            isRepetitionValid &&
            isStartDateValid &&
            isEndDateValid
      );
   }, [formData, validationMessages, endOfRepitition]);

   const handleCloseDialog = () => {
      setActiveStep(0);
      setUsersFormData(initialUsersFormData);
      setValidationMessages({
         rosterName: '',
         repetition: '',
         startDate: '',
         endDate: '',
      });
      onClose();
   };

   const calculateEndDate = (startDate, repetition) => {
      if (
         repetition !== '' &&
         repetition !== null &&
         !isNaN(repetition) &&
         repetition <= 5
      ) {
         return addDays(startDate, repetition * 7 - 1);
      }
      return addDays(startDate, 1 * 7 - 1);
   };

   const updateFormDataAndValidate = (name, value) => {
      setFormData((prevData) => {
         const updatedData = {
            ...prevData,
            [name]: value,
         };

         if (name === 'rosterName') {
            if (value === '') {
               setValidationMessages((prev) => ({
                  ...prev,
                  rosterName: 'Roster name is required',
               }));
            } else if (value.length > 128) {
               setValidationMessages((prev) => ({
                  ...prev,
                  rosterName: 'Maximum 128 characters allowed',
               }));
            } else {
               setValidationMessages((prev) => ({
                  ...prev,
                  rosterName: '',
               }));
            }
            updatedData.rosterName = value.slice(0, 128);
         }

         if (name === 'repetition') {
            if (value === '') {
               setValidationMessages((prevMessages) => ({
                  ...prevMessages,
                  repetition: t('common.repetition-required'),
               }));
            } else if ((isNaN(value) && value !== '') || parseInt(value) > 5) {
               setValidationMessages((prevMessages) => ({
                  ...prevMessages,
                  repetition:
                     parseInt(value) > 5
                        ? t('common.repetition-max')
                        : t('common.repetition-required'),
               }));
            } else {
               setValidationMessages((prevMessages) => ({
                  ...prevMessages,
                  repetition: '',
               }));
            }
         }

         if (name === 'endDate') {
            if (endOfRepitition) {
               const startDate = new Date(updatedData.startDate);
               const endDate = new Date(value);
               if (endDate <= startDate) {
                  setValidationMessages((prev) => ({
                     ...prev,
                     endDate: t('common.invalid-date'),
                  }));
               } else {
                  setValidationMessages((prev) => ({ ...prev, endDate: '' }));
               }
            }
         }

         if (name === 'startDate') {
            if (endOfRepitition) {
               const startDate = new Date(value);
               const endDate = new Date(updatedData.endDate);

               if (!endDate || endDate <= startDate) {
                  setValidationMessages((prev) => ({
                     ...prev,
                     endDate: t('common.invalid-date'),
                  }));
               } else {
                  setValidationMessages((prev) => ({ ...prev, endDate: '' }));
               }
            }
         }

         if (endOfRepitition && (name === 'startDate' || name === 'repetition')) {
            const start = new Date(
               name === 'startDate' ? value : updatedData.startDate
            );
            const rep = parseInt(
               name === 'repetition' ? value : updatedData.repetition,
               10
            );
            const safeRep = isNaN(rep) ? 1 : Math.min(Math.max(rep, 1), 5);
            updatedData.endDate = calculateEndDate(start, safeRep);
            setValidationMessages((m) => ({ ...m, endDate: '' }));
         }

         return updatedData;
      });
   };

   const handleInputChange = (e) => {
      const { name, value } = e.target;
      updateFormDataAndValidate(name, value);
   };

   const handleUsersInputChange = (e) => {
      const { name, value } = e.target;

      setUsersFormData((prevData) => {
         if (name === 'user') {
            return { ...prevData, userId: value };
         }
         if (name === 'group') {
            return { ...prevData, groupId: value };
         }
         return { ...prevData, [name]: value };
      });
   };

   const handleRemoveUser = (removedUserId) => {
      setUsersFormData((prevData) => ({
         ...prevData,
         userId: prevData.userId.filter((id) => id !== removedUserId),
      }));
   };

   const handleRemoveGroup = (removedGroupId) => {
      setUsersFormData((prevData) => ({
         ...prevData,
         groupId: prevData.groupId.filter((id) => id !== removedGroupId),
      }));
   };

   const handleChangeEndOfRepitition = (event) => {
      const next = event.target.value === 'true';
      setEndOfRepitition(next);
      if (!next) {
         setValidationMessages((m) => ({ ...m, endDate: '' }));
         setIsError((e) => ({ ...e, endDate: null }));
      } else {
         const { startDate, repetition } = formData;
         const rep = Math.min(Math.max(parseInt(repetition, 10) || 1, 1), 5);
         const autoEnd = calculateEndDate(new Date(startDate), rep);
         setFormData((prev) => ({ ...prev, endDate: autoEnd }));

         if (!autoEnd || autoEnd <= new Date(startDate)) {
            setValidationMessages((m) => ({
               ...m,
               endDate: t('common.invalid-date'),
            }));
            setIsError((e) => ({ ...e, endDate: true }));
         } else {
            setValidationMessages((m) => ({ ...m, endDate: '' }));
            setIsError((e) => ({ ...e, endDate: null }));
         }
      }
   };

   const buildRosterPayload = () => {
      const submitData = { ...formData };
      submitData.startDate = format(submitData.startDate, 'yyyy-MM-dd');
      submitData.rosterId = rosterIdAddUser;
      submitData.endBy = endOfRepitition
         ? format(submitData.endDate, 'yyyy-MM-dd')
         : null;
      submitData.langId = localStorage.getItem('i18nextLngId');
      return submitData;
   };

   const handleUpdateRoster = async () => {
      if (!isValid) {
         return;
      }

      show();
      try {
         const editResponse = await updateRoster(buildRosterPayload());

         if (editResponse.data.data.responseCode !== 0) {
            toast.error(editResponse.data.data.message);
         } else {
            toast.success(t('common.edit-roster-successfully'));
            handleRefreshDataAddRoster();
            onRefresh?.();
            setActiveStep(1);
         }
      } catch (error) {
         if (error.response) {
            if (error.response.status !== 401) {
               toast.error(t(error?.response.data));
            }
         }
      } finally {
         hide();
      }
   };

   const handleConfirmUsers = async (e) => {
      e?.preventDefault?.();

      const payload = buildUsersRosterPayload(
         rosterIdAddUser,
         usersFormData,
         allUser,
         GroupData
      );

      show();
      try {
         const response = await addUserRoster(payload);

         if (
            response.data.message === 'Successful' ||
            response.data.message === 'Thành công'
         ) {
            handleCloseDialog();
            toast.success(t('common.added-user-to-roster-successfully'));
            onRefresh?.();
         } else {
            toast.error(response.data.message);
         }
      } catch (error) {
         if (error.response) {
            if (error.response.status !== 401) {
               toast.error(t(error.response.data));
            }
         }
         console.error('There was an error adding users to roster!', error);
      } finally {
         hide();
      }
   };

   const isDoLaterDisabled = useMemo(() => {
      if (activeStep !== 1) {
         return false;
      }
      return usersFormData.userId.length > 0 || usersFormData.groupId.length > 0;
   }, [activeStep, usersFormData]);

   const handleDoLater = () => {
      if (isDoLaterDisabled) {
         return;
      }
      handleCloseDialog();
   };

   const renderStep0 = () => (
      <div className="space-y-4 pt-2">
         <div>
            <CustomInput
               required
               title={t('rosterWizard.roster-name')}
               inputType="text"
               name="rosterName"
               id="editRosterName"
               placeholder={t('rosterWizard.roster-name')}
               message={validationMessages.rosterName}
               handleInputChange={handleInputChange}
               value={formData.rosterName}
               inputProps={{ maxLength: 128 }}
            />
            {/* <p className="text-xs text-gray-400 justify-end flex mt-1">
               {formData.rosterName.length}/128
            </p> */}
         </div>

         <div>
            <label className="block text-sm font-medium mb-2">
               {t('rosterWizard.repetition-weekly')}{' '}
               <span className="text-red-500">*</span>
            </label>
            <div className="relative">
               <div className="flex items-center">
                  <div className="flex flex-grow items-center relative">
                     <input
                        type="number"
                        min={1}
                        max={5}
                        name="repetition"
                        id="editRepetition"
                        className={`form-control !rounded-[8px] !h-10 ${
                           isError.repetition && validationMessages.repetition ? 'invalid' : ''
                        } ${isError.repetition && validationMessages.repetition ? 'pr-12' : ''}`}
                        onChange={(e) => {
                           let val = parseInt(e.target.value, 10);
                           if (isNaN(val) || val < 1) val = 1;
                           if (val > 5) val = 5;
                           handleInputChange({
                              target: { name: e.target.name, value: val },
                           });
                        }}
                        value={formData.repetition}
                        placeholder="1"
                     />
                     {isError.repetition && validationMessages.repetition && (
                        <span className="absolute inset-y-0 right-0 px-3 py-3">
                           <ErrorIcon fontSize="small" className="text-red-500" />
                        </span>
                     )}
                  </div>
               </div>
               {isError.repetition && (
                  <p className="text-[14px] text-red-500 mt-1">
                     {validationMessages.repetition}
                  </p>
               )}
            </div>
         </div>

         <div>
            <label className="block text-sm font-medium mb-2">
               {t('common.start-date')} <span className="text-red-500">*</span>
            </label>
            <CustomDateTimePicker
               mode="single"
               value={formData.startDate}
               onChange={(newValue) =>
                  handleInputChange({
                     target: {
                        name: 'startDate',
                        value: newValue,
                     },
                  })
               }
               disableTime
               showOnlyDate
            />
         </div>

         <div>
            <label className="block text-sm font-medium mb-2">
               {t('common.end-of-repetition')}
            </label>
            <div className="flex flex-wrap items-center gap-4">
               <FormControl component="fieldset">
                  <RadioGroup
                     row
                     aria-label="End of repitition"
                     name="endOfRepitition"
                     value={endOfRepitition}
                     onChange={handleChangeEndOfRepitition}
                  >
                     <FormControlLabel
                        value={false}
                        control={<Radio size="small" />}
                        label={t('common.no-end-date')}
                     />
                     <FormControlLabel
                        value={true}
                        control={<Radio size="small" />}
                        label={t('common.end-by')}
                     />
                  </RadioGroup>
               </FormControl>

               {endOfRepitition && (
                  <div className="min-w-[180px]">
                     <CustomDateTimePicker
                        mode="single"
                        value={formData.endDate}
                        onChange={(newValue) =>
                           handleInputChange({
                              target: {
                                 name: 'endDate',
                                 value: newValue,
                              },
                           })
                        }
                        minDate={formData.startDate}
                        disableTime
                        showOnlyDate
                     />
                  </div>
               )}
            </div>

            {/* {endOfRepitition && isError.endDate && (
               <p className="text-[14px] text-red-500 mt-1">{validationMessages.endDate}</p>
            )}

            {endOfRepitition && (
               <p className="pt-2 text-[#3B82F6] text-sm">
                  {t('common.ends-on')}{' '}
                  {format(new Date(formData.endDate), settings.dateFormat)}
               </p>
            )} */}
         </div>
      </div>
   );

   return (
      <CustomDialog maxWidth="lg" fullWidth open onClose={handleCloseDialog} keepMounted>
         <DialogTitle>
            <div className="flex justify-between items-center">
               <p className="text-xl font-semibold">{t('common.edit-roster')}</p>
               <GrayIconButton onClick={handleCloseDialog}>
                  <CloseIcon />
               </GrayIconButton>
            </div>
         </DialogTitle>

         <DialogContent data-tour="shift-and-roster-edit-roster-input">
            <div
              className="mt-4 mb-6"
              
            >
               <Stepper activeStep={activeStep} alternativeLabel sx={stepperSx}>
                  {steps.map((label) => (
                     <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                     </Step>
                  ))}
               </Stepper>
            </div>

            {activeStep === 0 && renderStep0()}
            {activeStep === 1 && (
               <RosterUsersGroupsStep
                  formData={usersFormData}
                  onInputChange={handleUsersInputChange}
                  allUser={allUser}
                  GroupData={GroupData}
                  onRemoveUser={handleRemoveUser}
                  onRemoveGroup={handleRemoveGroup}
               />
            )}
         </DialogContent>

         <DialogActions>
            <div className="flex w-full justify-between items-center gap-2 px-4 py-4">
               {activeStep === 0 ? (
                  <ControlpanelSecondaryButton onClick={handleCloseDialog}>
                     <p>{t('button.cancel')}</p>
                  </ControlpanelSecondaryButton>
               ) : (
                  <span />
               )}

               <div className="flex items-center gap-3 ml-auto">
                  {activeStep === 1 && (
                     <ControlpanelSuccess3thButton
                        onClick={handleDoLater}
                        disabled={isDoLaterDisabled}
                     >
                        <p>{t('button.do-later')}</p>
                     </ControlpanelSuccess3thButton>
                  )}

                  {activeStep < STEP_COUNT - 1 ? (
                     <ControlpanelPrimaryButton onClick={handleUpdateRoster} disabled={!isValid}>
                        <p>{t('button.next')}</p>
                     </ControlpanelPrimaryButton>
                  ) : (
                     <ControlpanelPrimaryButton onClick={handleConfirmUsers}>
                        <p>{t('button.confirm')}</p>
                     </ControlpanelPrimaryButton>
                  )}
               </div>
            </div>
         </DialogActions>
      </CustomDialog>
   );
};

export default EditRosterDialog;
